import { useState } from "react";
import {
  CURRENCIES,
  CURRENCY_LABELS,
  INVENTORY_KIND_LABELS,
  normaliseCurrency,
  type Currency,
  MEMBER_ROLE_LABELS,
  type InventoryKind,
  type InventoryMembership,
  type SessionUser,
} from "@iltocat/shared";
import { ApiError, api } from "../lib/api.js";
import { switchInventory } from "../lib/activeInventory.js";
import { Icon } from "./Icon.js";
import { inventoryLabel } from "./InventorySwitcher.js";
import { Notice } from "./Notice.js";
import { Panel } from "./Panel.js";
import { Segmented } from "@vcnet/ui";
import { useToast } from "./Toast.js";

/**
 * Every inventory this account can reach, what can be done with each, and the
 * "new inventory" flow.
 *
 * An owner renames any of theirs, sets the currency its amounts are written
 * in (relabelled, never converted), and archives any but the home one. Archiving
 * is the only way to delete: an archived inventory is read-only and out of the
 * switcher but still opens (so it can be exported), and only then does
 * "Delete for good" appear. An editor can leave.
 *
 * A stock inventory needs the stock add-on on this account; without it the
 * kind is not offered, and a line says where it comes from.
 */
export function InventoriesPanel({ user }: { user: SessionUser }) {
  const [memberships, setMemberships] = useState<InventoryMembership[]>(user.memberships);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<InventoryKind>(user.stockEnabled ? "stock" : "personal");
  // A new inventory starts in the currency of the one being viewed: an
  // owner outside the US should not have to pick theirs every time.
  const [currency, setCurrency] = useState<Currency>(normaliseCurrency(user.activeCurrency));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string; currency: Currency } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const toast = useToast();

  async function create(event: React.FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const { inventoryId } = await api.createInventory(name.trim(), kind, currency);
      switchInventory(inventoryId);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Something went wrong.");
      setBusy(false);
    }
  }

  /**
   * Runs one management call and adopts the list it answers with. When the
   * inventory being viewed is the one that changed, the page reloads -- or
   * goes home, if it is gone -- because every screen belongs to it.
   */
  async function act(
    id: string,
    call: () => Promise<{ memberships: InventoryMembership[] }>,
    said: string,
    after: "stay" | "reload" | "home" = "stay",
  ) {
    setBusy(true);
    setError(null);
    try {
      const { memberships: next } = await call();
      if (id === user.activeInventoryId && after === "home") return switchInventory(user.inventoryId);
      if (id === user.activeInventoryId && after === "reload") return window.location.reload();
      setMemberships(next);
      setRenaming(null);
      setConfirming(null);
      toast(said);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  const live = memberships.filter((membership) => membership.archivedAt === null);
  const archived = memberships.filter((membership) => membership.archivedAt !== null);

  const row = (membership: InventoryMembership) => {
    const owner = membership.role === "owner";
    const home = membership.id === user.inventoryId;
    const isArchived = membership.archivedAt !== null;
    const label = inventoryLabel(membership);

    if (renaming?.id === membership.id) {
      return (
        <li key={membership.id} className="row-item">
          <form
            className="inline-add inline-add-wrap inventory-rename"
            onSubmit={(event) => {
              event.preventDefault();
              const recurrency = renaming.currency !== normaliseCurrency(membership.currency);
              void act(
                membership.id,
                () =>
                  api.inventories.update(membership.id, {
                    name: renaming.name,
                    ...(recurrency ? { currency: renaming.currency } : {}),
                  }),
                "Saved.",
                // Every amount on screen is written in the viewed inventory's currency.
                recurrency ? "reload" : "stay",
              );
            }}
          >
            <input
              className="input"
              aria-label={`New name for ${label}`}
              value={renaming.name}
              // A rename is typed straight after pressing Edit.
              autoFocus
              onChange={(event) => setRenaming({ ...renaming, name: event.target.value })}
            />
            <CurrencySelect
              label={`Currency for ${label}`}
              value={renaming.currency}
              onChange={(next) => setRenaming({ ...renaming, currency: next })}
            />
            <button type="button" className="button button-quiet" onClick={() => setRenaming(null)}>
              Cancel
            </button>
            <button type="submit" className="button" disabled={busy || !renaming.name.trim()}>
              Save
            </button>
            {renaming.currency !== normaliseCurrency(membership.currency) && (
              <p className="muted small inventory-currency-note">
                Amounts already in it are relabelled as {CURRENCY_LABELS[renaming.currency]}s, not converted.
              </p>
            )}
          </form>
        </li>
      );
    }

    return (
      <li key={membership.id} className="row-item">
        <span className={`row-icon${membership.kind === "stock" ? " row-icon-stock" : ""}`}>
          <Icon name={membership.kind === "stock" ? "box" : "grid"} size={18} />
        </span>
        <span className="row-text">
          <strong>{label}</strong>
          <span className="muted small">
            {INVENTORY_KIND_LABELS[membership.kind]} · {MEMBER_ROLE_LABELS[membership.role]}
            {home && " · Home"} · {normaliseCurrency(membership.currency)}
          </span>
        </span>
        <span className="row-actions">
          {membership.id === user.activeInventoryId ? (
            <span className="pill pill-accent">Viewing</span>
          ) : (
            <button
              className="button button-ghost button-small"
              type="button"
              onClick={() => switchInventory(membership.id)}
            >
              Open
            </button>
          )}
          {owner && !isArchived && (
            <button
              className="button button-ghost button-small"
              type="button"
              disabled={busy}
              onClick={() =>
                setRenaming({
                  id: membership.id,
                  name: membership.name,
                  currency: normaliseCurrency(membership.currency),
                })
              }
            >
              Edit
            </button>
          )}
          {owner && !home && !isArchived && (
            <button
              className="button button-ghost button-small"
              type="button"
              disabled={busy}
              onClick={() =>
                void act(membership.id, () => api.inventories.archive(membership.id), "Archived.", "reload")
              }
            >
              Archive
            </button>
          )}
          {owner && isArchived && (
            <button
              className="button button-ghost button-small"
              type="button"
              disabled={busy}
              onClick={() =>
                void act(membership.id, () => api.inventories.restore(membership.id), "Restored.", "reload")
              }
            >
              Restore
            </button>
          )}
          {owner && isArchived && confirming !== membership.id && (
            <button
              className="button button-danger button-small"
              type="button"
              disabled={busy}
              onClick={() => setConfirming(membership.id)}
            >
              Delete…
            </button>
          )}
          {!owner && (
            <button
              className="button button-ghost button-small"
              type="button"
              disabled={busy}
              onClick={() =>
                void act(membership.id, () => api.inventories.leave(membership.id), "You left it.", "home")
              }
            >
              Leave
            </button>
          )}
        </span>
        {confirming === membership.id && (
          <div className="row-confirm">
            <Notice tone="error">
              {`Delete ${label} for good? Every item, photo${
                membership.kind === "stock" ? ", receipt, deployment and invoice file" : ""
              } in it goes, for everybody it was shared with. Export it first if you want a copy. This cannot be undone.`}
            </Notice>
            <div className="panel-foot">
              <button type="button" className="button button-quiet" onClick={() => setConfirming(null)}>
                Keep it
              </button>
              <button
                type="button"
                className="button button-danger"
                disabled={busy}
                onClick={() =>
                  void act(membership.id, () => api.inventories.remove(membership.id), "Deleted.", "home")
                }
              >
                {busy ? "Deleting…" : "Delete for good"}
              </button>
            </div>
          </div>
        )}
      </li>
    );
  };

  return (
    <Panel
      title="Inventories"
      note="A stock inventory adds receiving, clients, deployments and month-end billing. A personal one stays as it is. Archive one to put it away: it becomes read-only, and only then can it be deleted."
    >
      {error && <Notice tone="error">{error}</Notice>}
      <ul className="rows">{live.map(row)}</ul>

      {archived.length > 0 && (
        <>
          <h3 className="facts-title">Archived</h3>
          <ul className="rows">{archived.map(row)}</ul>
        </>
      )}

      <form className="inline-add inline-add-wrap" onSubmit={create}>
        <input
          className="input"
          aria-label="Name for the new inventory"
          placeholder={user.stockEnabled ? "Name it, like “Central stock”" : "Name it, like “Garage”"}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <CurrencySelect label="Currency for the new inventory" value={currency} onChange={setCurrency} />
        {user.stockEnabled && (
          <Segmented<InventoryKind>
            label="Kind of inventory"
            size="sm"
            value={kind}
            onChange={setKind}
            options={[
              { value: "stock", label: INVENTORY_KIND_LABELS.stock },
              { value: "personal", label: INVENTORY_KIND_LABELS.personal },
            ]}
          />
        )}
        <button className="button" type="submit" disabled={busy || !name.trim()}>
          <Icon name="plus" size={18} />
          {busy ? "Making…" : "New inventory"}
        </button>
      </form>
      {!user.stockEnabled && (
        <p className="muted small">
          Running a stockroom? Stock inventories (receiving, clients, deployments, billing) come with{" "}
          <strong>utucat stock</strong>. Ask whoever invited you to turn it on.
        </p>
      )}
    </Panel>
  );
}

function CurrencySelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Currency;
  onChange: (currency: Currency) => void;
}) {
  return (
    <select
      className="input input-currency"
      aria-label={label}
      value={value}
      onChange={(event) => onChange(normaliseCurrency(event.target.value))}
    >
      {CURRENCIES.map((code) => (
        <option key={code} value={code}>
          {code} · {CURRENCY_LABELS[code]}
        </option>
      ))}
    </select>
  );
}
